import { LocalImageFile } from '../core/ports'

const MAX_ATTACHMENT_SIZE_BYTES = 15 * 1024 * 1024
const DISALLOWED_IMAGE_TYPES = ['image/svg+xml']

// Same compression native ships via its picker: the chat server has no byte
// limit, so this is the only thing keeping uploads reasonably sized.
const MAX_DIMENSION_PX = 2048
const COMPRESSION_QUALITY = 0.8

export function isValidImageAttachment(file: File): boolean {
  return (
    file.type.startsWith('image/') &&
    !DISALLOWED_IMAGE_TYPES.includes(file.type) &&
    file.size <= MAX_ATTACHMENT_SIZE_BYTES
  )
}

// Every attachment goes through one path: decode, fit into the maximum
// square, re-encode as JPEG. The result is what the tray previews and what
// uploads, so the recipient sees exactly what the sender saw. Returns null
// when the file cannot be decoded as an image.
export async function toLocalImage(file: File): Promise<LocalImageFile | null> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return null
  }

  const scale = Math.min(
    1,
    MAX_DIMENSION_PX / Math.max(bitmap.width, bitmap.height),
  )
  const width = Math.round(bitmap.width * scale)
  const height = Math.round(bitmap.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) {
    bitmap.close()
    return null
  }
  // JPEG has no alpha; without the fill, transparent PNG regions encode as
  // black.
  context.fillStyle = '#fff'
  context.fillRect(0, 0, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', COMPRESSION_QUALITY),
  )
  if (!blob) {
    return null
  }

  return {
    uri: URL.createObjectURL(blob),
    fileName: toJpegName(file.name),
    contentType: 'image/jpeg',
    size: blob.size,
    width,
    height,
  }
}

function toJpegName(name: string): string {
  return `${(name || 'photo').replace(/\.[^.]+$/, '')}.jpg`
}
